import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import moment from "moment";

/* ==========================================================================
   CONSTANTS & COLOR PALETTE
   ========================================================================== */
const COLORS = {
  BRAND_BLUE: "2563EB",       // Primary header background
  BRAND_LIGHT_BLUE: "EFF6FF", // Header accent
  WHITE: "FFFFFF",
  DARK_TEXT: "1E293B",
  MUTED_TEXT: "64748B",
  BORDER_GRAY: "CBD5E1",
  YELLOW_HEADER: "FEF08A",    // Subheader
  GRAY_ROW: "F8FAFC",         // Alternate row background
  GREEN_APPROVED: "DCFCE7",   // Status: Approved
  GREEN_TEXT: "166534",
  AMBER_PENDING: "FEF3C7",    // Status: Pending
  AMBER_TEXT: "92400E",
  GRAY_CELL: "F1F5F9",
};

const THIN_BORDER = {
  top: { style: "thin", color: { argb: COLORS.BORDER_GRAY } },
  left: { style: "thin", color: { argb: COLORS.BORDER_GRAY } },
  bottom: { style: "thin", color: { argb: COLORS.BORDER_GRAY } },
  right: { style: "thin", color: { argb: COLORS.BORDER_GRAY } },
};

let lastExportTimestamp = "";
let exportSequence = 0;

/* ==========================================================================
   HELPER FUNCTIONS
   ========================================================================== */
function fillCell(cell, argbColor) {
  cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: argbColor },
  };
}

function setFontStyle(cell, { bold = false, size = 11, color = COLORS.DARK_TEXT, italic = false } = {}) {
  cell.font = {
    name: "Times New Roman",
    size,
    bold,
    italic,
    color: { argb: color },
  };
}

function setAlignment(cell, { horizontal = "center", vertical = "middle", wrapText = true, indent = 0 } = {}) {
  cell.alignment = { horizontal, vertical, wrapText, indent };
}

function applyBordersToRange(sheet, startRow, startCol, endRow, endCol) {
  for (let r = startRow; r <= endRow; r++) {
    for (let c = startCol; c <= endCol; c++) {
      sheet.getCell(r, c).border = THIN_BORDER;
    }
  }
}

function getExportTimestamp() {
  const timestamp = moment().format("YYYYMMDD_HHmmss_SSS");
  if (timestamp === lastExportTimestamp) {
    exportSequence += 1;
  } else {
    lastExportTimestamp = timestamp;
    exportSequence = 0;
  }
  return exportSequence > 0 ? `${timestamp}_${exportSequence}` : timestamp;
}

function getClassSheetEvaluation(student) {
  return student?.evaluation || (Array.isArray(student?.evaluations) ? student.evaluations[0] : null);
}

function getFileCount(evaluation) {
  if (Array.isArray(evaluation?.files)) return evaluation.files.length;
  return Number(evaluation?.fileCount) || 0;
}

function getReplyCount(evaluation) {
  if (Array.isArray(evaluation?.replies)) return evaluation.replies.length;
  return Number(evaluation?.replyCount) || 0;
}

/**
 * Chuẩn hóa tên sheet Excel (Tối đa 31 ký tự, không chứa ký tự cấm: \ / ? * [ ] :)
 */
export function sanitizeSheetName(name, index = 1) {
  if (!name) return `Hoc sinh ${index}`;
  const clean = name.replace(/[\\/?*[\]:]/g, "").trim();
  const maxLen = 31;
  const prefix = `${index}. `;
  if ((prefix + clean).length <= maxLen) {
    return prefix + clean;
  }
  return (prefix + clean).substring(0, maxLen);
}

/**
 * Trích xuất danh sách danh mục nhận xét ngày động
 */
function extractDailyCategories(dataList) {
  const categories = new Map();
  dataList.forEach((item) => {
    const evaluations = item.evaluation
      ? [item.evaluation]
      : (Array.isArray(item.evaluations) ? item.evaluations : []);
    evaluations.forEach((ev) => {
      if (Array.isArray(ev.items) && ev.items.length > 0) {
        ev.items.forEach((cat) => {
          if (cat.categoryId && cat.categoryName && !categories.has(cat.categoryId)) {
            categories.set(cat.categoryId, cat.categoryName);
          }
        });
      }
    });
  });

  if (categories.size === 0) {
    // Fallback default categories
    return [
      { id: "learn", name: "Học tập" },
      { id: "eat", name: "Ăn uống" },
      { id: "sleep", name: "Ngủ nghỉ" },
      { id: "sanitary", name: "Vệ sinh" },
      { id: "health", name: "Sức khỏe" },
      { id: "common", name: "Nhận xét chung" },
    ];
  }

  return Array.from(categories.entries()).map(([id, name]) => ({ id, name }));
}

function getDailyCategoryContent(evaluation, category) {
  if (!evaluation) return "";
  if (Array.isArray(evaluation.items) && evaluation.items.length > 0) {
    const found = evaluation.items.find(
      (it) => it.categoryId === category.id || it.categoryName?.trim().toLowerCase() === category.name?.trim().toLowerCase()
    );
    return found ? found.content || "" : "";
  }
  // Fallback property mapping for legacy daily items
  if (category.id === "learn") return evaluation.learnContent || "";
  if (category.id === "eat") return evaluation.eatContent || "";
  if (category.id === "sleep") return evaluation.sleepContent || "";
  if (category.id === "sanitary") return evaluation.sanitaryContent || "";
  if (category.id === "health") return evaluation.healtContent || evaluation.healthContent || "";
  if (category.id === "common") return evaluation.commonContent || evaluation.content || "";
  return evaluation.content || "";
}

function renderBannerHeader(sheet, { title, schoolName, className, dateDisplay, extraInfo = "" }) {
  // Title row
  sheet.mergeCells("A1:K1");
  const titleCell = sheet.getCell("A1");
  titleCell.value = title.toUpperCase();
  setFontStyle(titleCell, { bold: true, size: 16, color: COLORS.BRAND_BLUE });
  setAlignment(titleCell, { horizontal: "center", vertical: "middle" });
  sheet.getRow(1).height = 36;

  // Subtitle rows
  sheet.mergeCells("A2:K2");
  const subCell1 = sheet.getCell("A2");
  subCell1.value = `Trường: ${schoolName || "OneKids"}     |     Lớp: ${className || "—"}     |     Thời gian: ${dateDisplay || "—"}`;
  setFontStyle(subCell1, { bold: true, size: 11, color: COLORS.DARK_TEXT });
  setAlignment(subCell1, { horizontal: "center", vertical: "middle" });
  sheet.getRow(2).height = 22;

  if (extraInfo) {
    sheet.mergeCells("A3:K3");
    const subCell2 = sheet.getCell("A3");
    subCell2.value = extraInfo;
    setFontStyle(subCell2, { italic: true, size: 10.5, color: COLORS.MUTED_TEXT });
    setAlignment(subCell2, { horizontal: "center", vertical: "middle" });
    sheet.getRow(3).height = 20;
    return 4; // Start row for table
  }
  return 4; // Start row for table
}

/* ==========================================================================
   DẠNG 1: XUẤT TỔNG HỢP CẢ LỚP (1 SHEET DUY NHẤT)
   ========================================================================== */

/**
 * 1.1 Xuất tổng hợp Nhận xét Ngày (1 Sheet)
 */
export async function exportDailyClassExcel({ classInfo = {}, date, dataList = [] }) {
  const workbook = new ExcelJS.Workbook();
  const dateFormatted = moment(date).format("DD/MM/YYYY");
  const sheet = workbook.addWorksheet(`Nhận xét ngày ${moment(date).format("DD-MM")}`);

  const startRow = renderBannerHeader(sheet, {
    title: "BẢNG TỔNG HỢP NHẬN XÉT HỌC SINH THEO NGÀY",
    schoolName: classInfo.schoolName,
    className: classInfo.className,
    dateDisplay: `Ngày ${dateFormatted}`,
  });

  const categories = extractDailyCategories(dataList);
  const headers = ["STT", "Họ và tên học sinh", "Tình trạng duyệt", ...categories.map((c) => c.name), "Số file", "Phản hồi"];

  const tableHeaderRow = startRow + 1;
  sheet.getRow(tableHeaderRow).height = 28;

  headers.forEach((headerText, colIdx) => {
    const cell = sheet.getCell(tableHeaderRow, colIdx + 1);
    cell.value = headerText;
    setFontStyle(cell, { bold: true, size: 11, color: COLORS.WHITE });
    fillCell(cell, COLORS.BRAND_BLUE);
    setAlignment(cell, { horizontal: "center", vertical: "middle" });
  });

  let currentRow = tableHeaderRow + 1;
  dataList.forEach((student, index) => {
    const evaluation = getClassSheetEvaluation(student);
    const isApproved = evaluation ? evaluation.approved === true || evaluation.status === "APPROVED" : false;
    const hasEval = Boolean(evaluation);

    sheet.getRow(currentRow).height = 42;
    // STT
    const sttCell = sheet.getCell(currentRow, 1);
    sttCell.value = index + 1;
    setFontStyle(sttCell, { size: 11 });
    setAlignment(sttCell, { horizontal: "center", vertical: "middle" });

    // Họ tên
    const nameCell = sheet.getCell(currentRow, 2);
    nameCell.value = student.studentName;
    setFontStyle(nameCell, { bold: true, size: 11 });
    setAlignment(nameCell, { horizontal: "left", vertical: "middle", indent: 1 });

    // Trạng thái duyệt
    const statusCell = sheet.getCell(currentRow, 3);
    if (!hasEval) {
      statusCell.value = "Chưa nhận xét";
      setFontStyle(statusCell, { italic: true, size: 10, color: COLORS.MUTED_TEXT });
    } else if (isApproved) {
      statusCell.value = "Đã duyệt";
      setFontStyle(statusCell, { bold: true, size: 10.5, color: COLORS.GREEN_TEXT });
      fillCell(statusCell, COLORS.GREEN_APPROVED);
    } else {
      statusCell.value = "Chờ duyệt";
      setFontStyle(statusCell, { bold: true, size: 10.5, color: COLORS.AMBER_TEXT });
      fillCell(statusCell, COLORS.AMBER_PENDING);
    }
    setAlignment(statusCell, { horizontal: "center", vertical: "middle" });

    // Các cột danh mục
    let colPointer = 4;
    categories.forEach((cat) => {
      const content = hasEval ? getDailyCategoryContent(evaluation, cat) : "";
      const catCell = sheet.getCell(currentRow, colPointer);
      catCell.value = content || "—";
      setFontStyle(catCell, { size: 10.5, color: content ? COLORS.DARK_TEXT : COLORS.MUTED_TEXT });
      setAlignment(catCell, { horizontal: "left", vertical: "middle", wrapText: true, indent: content ? 1 : 0 });
      colPointer++;
    });

    // File
    const fileCount = getFileCount(evaluation);
    const fileCell = sheet.getCell(currentRow, colPointer);
    fileCell.value = fileCount > 0 ? `${fileCount} file` : "—";
    setFontStyle(fileCell, { size: 10.5, color: fileCount > 0 ? COLORS.BRAND_BLUE : COLORS.MUTED_TEXT });
    setAlignment(fileCell, { horizontal: "center", vertical: "middle" });

    // Phản hồi
    const replyCount = getReplyCount(evaluation);
    const replyCell = sheet.getCell(currentRow, colPointer + 1);
    replyCell.value = replyCount > 0 ? `${replyCount} phản hồi` : "—";
    setFontStyle(replyCell, { size: 10.5, color: replyCount > 0 ? COLORS.BRAND_BLUE : COLORS.MUTED_TEXT });
    setAlignment(replyCell, { horizontal: "center", vertical: "middle" });

    currentRow++;
  });

  const totalCols = headers.length;
  applyBordersToRange(sheet, tableHeaderRow, 1, currentRow - 1, totalCols);

  // Column widths
  sheet.getColumn(1).width = 7;   // STT
  sheet.getColumn(2).width = 24;  // Tên HS
  sheet.getColumn(3).width = 16;  // Trạng thái
  for (let c = 4; c <= 3 + categories.length; c++) {
    sheet.getColumn(c).width = 28; // Danh mục
  }
  sheet.getColumn(totalCols - 1).width = 12; // File
  sheet.getColumn(totalCols).width = 14;     // Phản hồi

  sheet.views = [{ state: "frozen", ySplit: tableHeaderRow }];

  const buffer = await workbook.xlsx.writeBuffer();
  const fileName = `NHAN_XET_NGAY_${classInfo.className || "Lop"}_${moment(date).format("YYYY-MM-DD")}_${getExportTimestamp()}.xlsx`;
  saveAs(new Blob([buffer]), fileName);
}

/**
 * 1.2 Xuất tổng hợp Nhận xét Tuần (1 Sheet)
 */
export async function exportWeeklyClassExcel({ classInfo = {}, date, dataList = [] }) {
  const workbook = new ExcelJS.Workbook();
  const weekNumber = moment(date).isoWeek();
  const fromDate = moment(date).startOf("isoWeek").format("DD/MM/YYYY");
  const toDate = moment(date).endOf("isoWeek").format("DD/MM/YYYY");
  const sheet = workbook.addWorksheet(`Nhận xét Tuần ${weekNumber}`);

  const startRow = renderBannerHeader(sheet, {
    title: "BẢNG TỔNG HỢP NHẬN XÉT HỌC SINH THEO TUẦN",
    schoolName: classInfo.schoolName,
    className: classInfo.className,
    dateDisplay: `Tuần ${weekNumber} (${fromDate} – ${toDate})`,
  });

  const headers = ["STT", "Họ và tên học sinh", "Ngày tạo", "Tình trạng duyệt", "Nội dung nhận xét tuần", "Số file đính kèm", "Phản hồi"];
  const tableHeaderRow = startRow + 1;
  sheet.getRow(tableHeaderRow).height = 28;

  headers.forEach((headerText, colIdx) => {
    const cell = sheet.getCell(tableHeaderRow, colIdx + 1);
    cell.value = headerText;
    setFontStyle(cell, { bold: true, size: 11, color: COLORS.WHITE });
    fillCell(cell, COLORS.BRAND_BLUE);
    setAlignment(cell, { horizontal: "center", vertical: "middle" });
  });

  let currentRow = tableHeaderRow + 1;
  dataList.forEach((student, index) => {
    const evaluation = getClassSheetEvaluation(student);
    const isApproved = evaluation ? evaluation.approved === true || evaluation.status === "APPROVED" : false;
    const hasEval = Boolean(evaluation);

    sheet.getRow(currentRow).height = 42;
    sheet.getCell(currentRow, 1).value = index + 1;
    setAlignment(sheet.getCell(currentRow, 1), { horizontal: "center", vertical: "middle" });

    sheet.getCell(currentRow, 2).value = student.studentName;
    setFontStyle(sheet.getCell(currentRow, 2), { bold: true });
    setAlignment(sheet.getCell(currentRow, 2), { horizontal: "left", vertical: "middle", indent: 1 });

    sheet.getCell(currentRow, 3).value = evaluation?.createdDate ? moment(evaluation.createdDate).format("DD/MM/YYYY HH:mm") : "—";
    setAlignment(sheet.getCell(currentRow, 3), { horizontal: "center", vertical: "middle" });

    const statusCell = sheet.getCell(currentRow, 4);
    if (!hasEval) {
      statusCell.value = "Chưa nhận xét";
      setFontStyle(statusCell, { italic: true, color: COLORS.MUTED_TEXT });
    } else if (isApproved) {
      statusCell.value = "Đã duyệt";
      setFontStyle(statusCell, { bold: true, color: COLORS.GREEN_TEXT });
      fillCell(statusCell, COLORS.GREEN_APPROVED);
    } else {
      statusCell.value = "Chờ duyệt";
      setFontStyle(statusCell, { bold: true, color: COLORS.AMBER_TEXT });
      fillCell(statusCell, COLORS.AMBER_PENDING);
    }
    setAlignment(statusCell, { horizontal: "center", vertical: "middle" });

    const contentCell = sheet.getCell(currentRow, 5);
    contentCell.value = evaluation?.content || "—";
    setAlignment(contentCell, { horizontal: "left", vertical: "middle", wrapText: true, indent: evaluation?.content ? 1 : 0 });

    const fileCount = getFileCount(evaluation);
    sheet.getCell(currentRow, 6).value = fileCount > 0 ? `${fileCount} file` : "—";
    setAlignment(sheet.getCell(currentRow, 6), { horizontal: "center", vertical: "middle" });

    const replyCount = getReplyCount(evaluation);
    sheet.getCell(currentRow, 7).value = replyCount > 0 ? `${replyCount} phản hồi` : "—";
    setAlignment(sheet.getCell(currentRow, 7), { horizontal: "center", vertical: "middle" });

    currentRow++;
  });

  applyBordersToRange(sheet, tableHeaderRow, 1, currentRow - 1, headers.length);
  sheet.getColumn(1).width = 7;
  sheet.getColumn(2).width = 24;
  sheet.getColumn(3).width = 18;
  sheet.getColumn(4).width = 16;
  sheet.getColumn(5).width = 50;
  sheet.getColumn(6).width = 14;
  sheet.getColumn(7).width = 16;

  sheet.views = [{ state: "frozen", ySplit: tableHeaderRow }];

  const buffer = await workbook.xlsx.writeBuffer();
  const fileName = `NHAN_XET_TUAN_${classInfo.className || "Lop"}_Tuan_${weekNumber}_${moment(date).format("YYYY")}_${getExportTimestamp()}.xlsx`;
  saveAs(new Blob([buffer]), fileName);
}

/**
 * 1.3 Xuất tổng hợp Nhận xét Tháng (1 Sheet)
 */
export async function exportMonthlyClassExcel({ classInfo = {}, date, dataList = [] }) {
  const workbook = new ExcelJS.Workbook();
  const monthDisplay = moment(date, ["YYYY-MM", "YYYY-MM-DD"]).format("MM/YYYY");
  const sheet = workbook.addWorksheet(`Nhận xét Tháng ${moment(date, ["YYYY-MM", "YYYY-MM-DD"]).format("MM-YYYY")}`);

  const startRow = renderBannerHeader(sheet, {
    title: "BẢNG ĐÁNH GIÁ NHẬN XÉT HỌC SINH THEO THÁNG",
    schoolName: classInfo.schoolName,
    className: classInfo.className,
    dateDisplay: `Tháng ${monthDisplay}`,
  });

  const headers = ["STT", "Họ và tên học sinh", "Ngày tạo", "Tình trạng duyệt", "Nội dung nhận xét tháng", "Số file đính kèm", "Phản hồi"];
  const tableHeaderRow = startRow + 1;
  sheet.getRow(tableHeaderRow).height = 28;

  headers.forEach((headerText, colIdx) => {
    const cell = sheet.getCell(tableHeaderRow, colIdx + 1);
    cell.value = headerText;
    setFontStyle(cell, { bold: true, size: 11, color: COLORS.WHITE });
    fillCell(cell, COLORS.BRAND_BLUE);
    setAlignment(cell, { horizontal: "center", vertical: "middle" });
  });

  let currentRow = tableHeaderRow + 1;
  dataList.forEach((student, index) => {
    const evaluation = getClassSheetEvaluation(student);
    const isApproved = evaluation ? evaluation.approved === true || evaluation.status === "APPROVED" : false;
    const hasEval = Boolean(evaluation);

    sheet.getRow(currentRow).height = 42;
    sheet.getCell(currentRow, 1).value = index + 1;
    setAlignment(sheet.getCell(currentRow, 1), { horizontal: "center", vertical: "middle" });

    sheet.getCell(currentRow, 2).value = student.studentName;
    setFontStyle(sheet.getCell(currentRow, 2), { bold: true });
    setAlignment(sheet.getCell(currentRow, 2), { horizontal: "left", vertical: "middle", indent: 1 });

    sheet.getCell(currentRow, 3).value = evaluation?.createdDate ? moment(evaluation.createdDate).format("DD/MM/YYYY HH:mm") : "—";
    setAlignment(sheet.getCell(currentRow, 3), { horizontal: "center", vertical: "middle" });

    const statusCell = sheet.getCell(currentRow, 4);
    if (!hasEval) {
      statusCell.value = "Chưa nhận xét";
      setFontStyle(statusCell, { italic: true, color: COLORS.MUTED_TEXT });
    } else if (isApproved) {
      statusCell.value = "Đã duyệt";
      setFontStyle(statusCell, { bold: true, color: COLORS.GREEN_TEXT });
      fillCell(statusCell, COLORS.GREEN_APPROVED);
    } else {
      statusCell.value = "Chờ duyệt";
      setFontStyle(statusCell, { bold: true, color: COLORS.AMBER_TEXT });
      fillCell(statusCell, COLORS.AMBER_PENDING);
    }
    setAlignment(statusCell, { horizontal: "center", vertical: "middle" });

    const contentCell = sheet.getCell(currentRow, 5);
    contentCell.value = evaluation?.content || "—";
    setAlignment(contentCell, { horizontal: "left", vertical: "middle", wrapText: true, indent: evaluation?.content ? 1 : 0 });

    const fileCount = getFileCount(evaluation);
    sheet.getCell(currentRow, 6).value = fileCount > 0 ? `${fileCount} file` : "—";
    setAlignment(sheet.getCell(currentRow, 6), { horizontal: "center", vertical: "middle" });

    const replyCount = getReplyCount(evaluation);
    sheet.getCell(currentRow, 7).value = replyCount > 0 ? `${replyCount} phản hồi` : "—";
    setAlignment(sheet.getCell(currentRow, 7), { horizontal: "center", vertical: "middle" });

    currentRow++;
  });

  applyBordersToRange(sheet, tableHeaderRow, 1, currentRow - 1, headers.length);
  sheet.getColumn(1).width = 7;
  sheet.getColumn(2).width = 24;
  sheet.getColumn(3).width = 18;
  sheet.getColumn(4).width = 16;
  sheet.getColumn(5).width = 50;
  sheet.getColumn(6).width = 14;
  sheet.getColumn(7).width = 16;

  sheet.views = [{ state: "frozen", ySplit: tableHeaderRow }];

  const buffer = await workbook.xlsx.writeBuffer();
  const fileName = `NHAN_XET_THANG_${classInfo.className || "Lop"}_Thang_${moment(date, ["YYYY-MM", "YYYY-MM-DD"]).format("MM-YYYY")}_${getExportTimestamp()}.xlsx`;
  saveAs(new Blob([buffer]), fileName);
}

/**
 * 1.4 Xuất tổng hợp Nhận xét Định kỳ (1 Sheet Ma trận lần)
 */
export async function exportPeriodicClassExcel({ classInfo = {}, year, dataList = [] }) {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet(`Nhận xét Định kỳ ${year}`);

  const startRow = renderBannerHeader(sheet, {
    title: "BẢNG ĐÁNH GIÁ NHẬN XÉT ĐỊNH KỲ HỌC SINH",
    schoolName: classInfo.schoolName,
    className: classInfo.className,
    dateDisplay: `Năm học / Năm ${year}`,
  });

  // Xác định số lần tối đa có trong lớp
  let maxPeriods = 1;
  dataList.forEach((s) => {
    (s.evaluations || []).forEach((ev) => {
      if (ev.periodNumber > maxPeriods) maxPeriods = ev.periodNumber;
    });
  });

  const headerRow1 = startRow + 1;
  const headerRow2 = startRow + 2;
  sheet.getRow(headerRow1).height = 24;
  sheet.getRow(headerRow2).height = 24;

  // Fixed columns
  sheet.mergeCells(headerRow1, 1, headerRow2, 1);
  sheet.getCell(headerRow1, 1).value = "STT";

  sheet.mergeCells(headerRow1, 2, headerRow2, 2);
  sheet.getCell(headerRow1, 2).value = "Họ và tên học sinh";

  sheet.mergeCells(headerRow1, 3, headerRow2, 3);
  sheet.getCell(headerRow1, 3).value = "Trạng thái";

  sheet.mergeCells(headerRow1, 4, headerRow2, 4);
  sheet.getCell(headerRow1, 4).value = "Tổng số lần";

  sheet.mergeCells(headerRow1, 5, headerRow2, 5);
  sheet.getCell(headerRow1, 5).value = "Tổng thể";

  for (let c = 1; c <= 5; c++) {
    const cell = sheet.getCell(headerRow1, c);
    setFontStyle(cell, { bold: true, color: COLORS.WHITE });
    fillCell(cell, COLORS.BRAND_BLUE);
    setAlignment(cell, { horizontal: "center", vertical: "middle" });
  }

  // Dynamic period columns
  let colPointer = 6;
  for (let p = 1; p <= maxPeriods; p++) {
    sheet.mergeCells(headerRow1, colPointer, headerRow1, colPointer + 1);
    const pGroupCell = sheet.getCell(headerRow1, colPointer);
    pGroupCell.value = `LẦN ${p}`;
    setFontStyle(pGroupCell, { bold: true, color: COLORS.DARK_TEXT });
    fillCell(pGroupCell, COLORS.YELLOW_HEADER);
    setAlignment(pGroupCell, { horizontal: "center", vertical: "middle" });

    const statusSubCell = sheet.getCell(headerRow2, colPointer);
    statusSubCell.value = "Trạng thái";
    setFontStyle(statusSubCell, { bold: true, size: 10.5 });
    fillCell(statusSubCell, COLORS.GRAY_CELL);
    setAlignment(statusSubCell, { horizontal: "center", vertical: "middle" });

    const contentSubCell = sheet.getCell(headerRow2, colPointer + 1);
    contentSubCell.value = "Nội dung nhận xét";
    setFontStyle(contentSubCell, { bold: true, size: 10.5 });
    fillCell(contentSubCell, COLORS.GRAY_CELL);
    setAlignment(contentSubCell, { horizontal: "center", vertical: "middle" });

    colPointer += 2;
  }

  // Phản hồi column
  sheet.mergeCells(headerRow1, colPointer, headerRow2, colPointer);
  const replyHeaderCell = sheet.getCell(headerRow1, colPointer);
  replyHeaderCell.value = "Tổng phản hồi";
  setFontStyle(replyHeaderCell, { bold: true, color: COLORS.WHITE });
  fillCell(replyHeaderCell, COLORS.BRAND_BLUE);
  setAlignment(replyHeaderCell, { horizontal: "center", vertical: "middle" });

  const totalCols = colPointer;
  let currentRow = headerRow2 + 1;

  dataList.forEach((student, index) => {
    sheet.getRow(currentRow).height = 40;

    sheet.getCell(currentRow, 1).value = index + 1;
    setAlignment(sheet.getCell(currentRow, 1), { horizontal: "center", vertical: "middle" });

    sheet.getCell(currentRow, 2).value = student.studentName;
    setFontStyle(sheet.getCell(currentRow, 2), { bold: true });
    setAlignment(sheet.getCell(currentRow, 2), { horizontal: "left", vertical: "middle", indent: 1 });

    sheet.getCell(currentRow, 3).value = student.kidStatus === "STUDYING" ? "Đang học" : "Đã chuyển";
    setFontStyle(sheet.getCell(currentRow, 3), { size: 10.5, italic: student.kidStatus !== "STUDYING" });
    setAlignment(sheet.getCell(currentRow, 3), { horizontal: "center", vertical: "middle" });

    const evals = student.evaluations || [];
    sheet.getCell(currentRow, 4).value = evals.length;
    setFontStyle(sheet.getCell(currentRow, 4), { bold: true, color: COLORS.BRAND_BLUE });
    setAlignment(sheet.getCell(currentRow, 4), { horizontal: "center", vertical: "middle" });

    // Overall status
    const allApproved = evals.length > 0 && evals.every((e) => e.approved === true);
    const hasPending = evals.some((e) => e.approved !== true);
    const overallCell = sheet.getCell(currentRow, 5);
    if (evals.length === 0) {
      overallCell.value = "Chưa có";
      setFontStyle(overallCell, { color: COLORS.MUTED_TEXT });
    } else if (allApproved) {
      overallCell.value = "Đã duyệt";
      setFontStyle(overallCell, { bold: true, color: COLORS.GREEN_TEXT });
      fillCell(overallCell, COLORS.GREEN_APPROVED);
    } else if (hasPending) {
      overallCell.value = "Chờ duyệt";
      setFontStyle(overallCell, { bold: true, color: COLORS.AMBER_TEXT });
      fillCell(overallCell, COLORS.AMBER_PENDING);
    }
    setAlignment(overallCell, { horizontal: "center", vertical: "middle" });

    // Periods
    let pCol = 6;
    for (let p = 1; p <= maxPeriods; p++) {
      const pEval = evals.find((e) => e.periodNumber === p);
      const pStatusCell = sheet.getCell(currentRow, pCol);
      const pContentCell = sheet.getCell(currentRow, pCol + 1);

      if (pEval) {
        if (pEval.approved) {
          pStatusCell.value = "Đã duyệt";
          setFontStyle(pStatusCell, { bold: true, color: COLORS.GREEN_TEXT, size: 10 });
          fillCell(pStatusCell, COLORS.GREEN_APPROVED);
        } else {
          pStatusCell.value = "Chờ duyệt";
          setFontStyle(pStatusCell, { bold: true, color: COLORS.AMBER_TEXT, size: 10 });
          fillCell(pStatusCell, COLORS.AMBER_PENDING);
        }
        pContentCell.value = pEval.content || "—";
      } else {
        pStatusCell.value = "—";
        pContentCell.value = "—";
        setFontStyle(pStatusCell, { color: COLORS.MUTED_TEXT });
        setFontStyle(pContentCell, { color: COLORS.MUTED_TEXT });
      }
      setAlignment(pStatusCell, { horizontal: "center", vertical: "middle" });
      setAlignment(pContentCell, { horizontal: "left", vertical: "middle", wrapText: true, indent: pEval?.content ? 1 : 0 });

      pCol += 2;
    }

    const totalReplies = evals.reduce((sum, e) => sum + getReplyCount(e), 0);
    sheet.getCell(currentRow, totalCols).value = totalReplies > 0 ? `${totalReplies}` : "0";
    setAlignment(sheet.getCell(currentRow, totalCols), { horizontal: "center", vertical: "middle" });

    currentRow++;
  });

  applyBordersToRange(sheet, headerRow1, 1, currentRow - 1, totalCols);
  sheet.getColumn(1).width = 7;
  sheet.getColumn(2).width = 24;
  sheet.getColumn(3).width = 14;
  sheet.getColumn(4).width = 12;
  sheet.getColumn(5).width = 15;
  for (let p = 1; p <= maxPeriods; p++) {
    sheet.getColumn(4 + p * 2).width = 14; // Trạng thái lần
    sheet.getColumn(5 + p * 2).width = 38; // Nội dung lần
  }
  sheet.getColumn(totalCols).width = 14;

  sheet.views = [{ state: "frozen", ySplit: headerRow2 }];

  const buffer = await workbook.xlsx.writeBuffer();
  const fileName = `NHAN_XET_DINH_KY_${classInfo.className || "Lop"}_Nam_${year}_${getExportTimestamp()}.xlsx`;
  saveAs(new Blob([buffer]), fileName);
}

/* ==========================================================================
   DẠNG 2: XUẤT CHI TIẾT TỪNG HỌC SINH (MỖI HỌC SINH LÀ 1 SHEET RIÊNG)
   ========================================================================== */

/**
 * 2.1 Xuất nhật ký Ngày Multi-Sheet (Mỗi HS là 1 Sheet)
 */
export async function exportDailyStudentSheetsExcel({ classInfo = {}, dateRange = [], studentsData = [] }) {
  const workbook = new ExcelJS.Workbook();
  const fromFormatted = moment(dateRange[0]).format("DD/MM/YYYY");
  const toFormatted = moment(dateRange[1]).format("DD/MM/YYYY");

  studentsData.forEach((student, index) => {
    const sheetName = sanitizeSheetName(student.studentName, index + 1);
    const sheet = workbook.addWorksheet(sheetName);

    const startRow = renderBannerHeader(sheet, {
      title: "SỔ NHẬT KÝ THEO DÕI NHẬN XÉT NGÀY CỦA HỌC SINH",
      schoolName: classInfo.schoolName,
      className: classInfo.className,
      dateDisplay: `Từ ${fromFormatted} đến ${toFormatted}`,
      extraInfo: `Học sinh: ${student.studentName}    |    Mã HS: ${student.studentCode || `HS${student.studentId || index + 1}`}`,
    });

    const dailyList = student.dailyEvaluations || [];
    const categories = extractDailyCategories(dailyList.map((e) => ({ evaluations: [e] })));
    const headers = ["STT", "Ngày", "Tình trạng duyệt", ...categories.map((c) => c.name), "Giáo viên nhận xét", "File", "Phản hồi"];

    const tableHeaderRow = startRow + 1;
    sheet.getRow(tableHeaderRow).height = 28;

    headers.forEach((headerText, colIdx) => {
      const cell = sheet.getCell(tableHeaderRow, colIdx + 1);
      cell.value = headerText;
      setFontStyle(cell, { bold: true, size: 11, color: COLORS.WHITE });
      fillCell(cell, COLORS.BRAND_BLUE);
      setAlignment(cell, { horizontal: "center", vertical: "middle" });
    });

    let currentRow = tableHeaderRow + 1;
    if (dailyList.length === 0) {
      sheet.mergeCells(currentRow, 1, currentRow, headers.length);
      const emptyCell = sheet.getCell(currentRow, 1);
      emptyCell.value = "Chưa có dữ liệu nhận xét trong khoảng thời gian này.";
      setFontStyle(emptyCell, { italic: true, color: COLORS.MUTED_TEXT });
      setAlignment(emptyCell, { horizontal: "center", vertical: "middle" });
      currentRow++;
    } else {
      dailyList.forEach((ev, idx) => {
        sheet.getRow(currentRow).height = 38;
        sheet.getCell(currentRow, 1).value = idx + 1;
        setAlignment(sheet.getCell(currentRow, 1), { horizontal: "center", vertical: "middle" });

        sheet.getCell(currentRow, 2).value = moment(ev.date).format("DD/MM/YYYY");
        setFontStyle(sheet.getCell(currentRow, 2), { bold: true });
        setAlignment(sheet.getCell(currentRow, 2), { horizontal: "center", vertical: "middle" });

        const isApproved = ev.approved === true || ev.status === "APPROVED";
        const statusCell = sheet.getCell(currentRow, 3);
        statusCell.value = isApproved ? "Đã duyệt" : "Chờ duyệt";
        setFontStyle(statusCell, { bold: true, color: isApproved ? COLORS.GREEN_TEXT : COLORS.AMBER_TEXT, size: 10 });
        fillCell(statusCell, isApproved ? COLORS.GREEN_APPROVED : COLORS.AMBER_PENDING);
        setAlignment(statusCell, { horizontal: "center", vertical: "middle" });

        let colPointer = 4;
        categories.forEach((cat) => {
          const content = getDailyCategoryContent(ev, cat);
          const catCell = sheet.getCell(currentRow, colPointer);
          catCell.value = content || "—";
          setAlignment(catCell, { horizontal: "left", vertical: "middle", wrapText: true, indent: content ? 1 : 0 });
          colPointer++;
        });

        sheet.getCell(currentRow, colPointer).value = ev.teacherName || ev.createdBy || "Giáo viên";
        setAlignment(sheet.getCell(currentRow, colPointer), { horizontal: "center", vertical: "middle" });

        const fileCount = getFileCount(ev);
        sheet.getCell(currentRow, colPointer + 1).value = fileCount > 0 ? `${fileCount} file` : "—";
        setAlignment(sheet.getCell(currentRow, colPointer + 1), { horizontal: "center", vertical: "middle" });

        const replyCount = getReplyCount(ev);
        const replyText = replyCount > 0 ? `${replyCount} phản hồi` : "—";
        sheet.getCell(currentRow, colPointer + 2).value = replyText;
        setAlignment(sheet.getCell(currentRow, colPointer + 2), { horizontal: "center", vertical: "middle" });

        currentRow++;
      });
    }

    const totalCols = headers.length;
    applyBordersToRange(sheet, tableHeaderRow, 1, currentRow - 1, totalCols);
    sheet.getColumn(1).width = 7;
    sheet.getColumn(2).width = 15;
    sheet.getColumn(3).width = 14;
    for (let c = 4; c <= 3 + categories.length; c++) {
      sheet.getColumn(c).width = 28;
    }
    sheet.getColumn(totalCols - 2).width = 18; // GV
    sheet.getColumn(totalCols - 1).width = 12; // File
    sheet.getColumn(totalCols).width = 15;     // Phản hồi

    sheet.views = [{ state: "frozen", ySplit: tableHeaderRow }];
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const fileName = `SO_NHAT_KY_NGAY_${classInfo.className || "Lop"}_${moment(dateRange[0]).format("YYYYMMDD")}_${moment(dateRange[1]).format("YYYYMMDD")}_${getExportTimestamp()}.xlsx`;
  saveAs(new Blob([buffer]), fileName);
}

/**
 * 2.2 Xuất sổ theo dõi Tuần Multi-Sheet (Mỗi HS là 1 Sheet)
 */
export async function exportWeeklyStudentSheetsExcel({ classInfo = {}, dateRange = [], studentsData = [] }) {
  const workbook = new ExcelJS.Workbook();
  const fromFormatted = moment(dateRange[0]).format("DD/MM/YYYY");
  const toFormatted = moment(dateRange[1]).format("DD/MM/YYYY");

  studentsData.forEach((student, index) => {
    const sheetName = sanitizeSheetName(student.studentName, index + 1);
    const sheet = workbook.addWorksheet(sheetName);

    const startRow = renderBannerHeader(sheet, {
      title: "SỔ THEO DÕI ĐÁNH GIÁ NHẬN XÉT THEO TUẦN",
      schoolName: classInfo.schoolName,
      className: classInfo.className,
      dateDisplay: `Từ ${fromFormatted} đến ${toFormatted}`,
      extraInfo: `Học sinh: ${student.studentName}    |    Mã HS: ${student.studentCode || `HS${student.studentId || index + 1}`}`,
    });

    const weeklyList = student.weeklyEvaluations || [];
    const headers = ["STT", "Tuần", "Khoảng thời gian (Từ – Đến)", "Trạng thái", "Nội dung nhận xét tuần", "Giáo viên", "File", "Phản hồi"];

    const tableHeaderRow = startRow + 1;
    sheet.getRow(tableHeaderRow).height = 28;

    headers.forEach((headerText, colIdx) => {
      const cell = sheet.getCell(tableHeaderRow, colIdx + 1);
      cell.value = headerText;
      setFontStyle(cell, { bold: true, size: 11, color: COLORS.WHITE });
      fillCell(cell, COLORS.BRAND_BLUE);
      setAlignment(cell, { horizontal: "center", vertical: "middle" });
    });

    let currentRow = tableHeaderRow + 1;
    if (weeklyList.length === 0) {
      sheet.mergeCells(currentRow, 1, currentRow, headers.length);
      const emptyCell = sheet.getCell(currentRow, 1);
      emptyCell.value = "Chưa có dữ liệu nhận xét tuần trong khoảng thời gian này.";
      setFontStyle(emptyCell, { italic: true, color: COLORS.MUTED_TEXT });
      setAlignment(emptyCell, { horizontal: "center", vertical: "middle" });
      currentRow++;
    } else {
      weeklyList.forEach((ev, idx) => {
        sheet.getRow(currentRow).height = 42;
        sheet.getCell(currentRow, 1).value = idx + 1;
        setAlignment(sheet.getCell(currentRow, 1), { horizontal: "center", vertical: "middle" });

        sheet.getCell(currentRow, 2).value = `Tuần ${ev.weekNumber || idx + 1}`;
        setFontStyle(sheet.getCell(currentRow, 2), { bold: true });
        setAlignment(sheet.getCell(currentRow, 2), { horizontal: "center", vertical: "middle" });

        const fromW = ev.fromDate ? moment(ev.fromDate).format("DD/MM/YYYY") : "—";
        const toW = ev.toDate ? moment(ev.toDate).format("DD/MM/YYYY") : "—";
        sheet.getCell(currentRow, 3).value = `${fromW} – ${toW}`;
        setAlignment(sheet.getCell(currentRow, 3), { horizontal: "center", vertical: "middle" });

        const isApproved = ev.approved === true || ev.status === "APPROVED";
        const statusCell = sheet.getCell(currentRow, 4);
        statusCell.value = isApproved ? "Đã duyệt" : "Chờ duyệt";
        setFontStyle(statusCell, { bold: true, color: isApproved ? COLORS.GREEN_TEXT : COLORS.AMBER_TEXT, size: 10 });
        fillCell(statusCell, isApproved ? COLORS.GREEN_APPROVED : COLORS.AMBER_PENDING);
        setAlignment(statusCell, { horizontal: "center", vertical: "middle" });

        const contentCell = sheet.getCell(currentRow, 5);
        contentCell.value = ev.content || "—";
        setAlignment(contentCell, { horizontal: "left", vertical: "middle", wrapText: true, indent: 1 });

        sheet.getCell(currentRow, 6).value = ev.teacherName || ev.createdBy || "Giáo viên";
        setAlignment(sheet.getCell(currentRow, 6), { horizontal: "center", vertical: "middle" });

        const fileCount = getFileCount(ev);
        sheet.getCell(currentRow, 7).value = fileCount > 0 ? `${fileCount} file` : "—";
        setAlignment(sheet.getCell(currentRow, 7), { horizontal: "center", vertical: "middle" });

        const replyCount = getReplyCount(ev);
        sheet.getCell(currentRow, 8).value = replyCount > 0 ? `${replyCount} phản hồi` : "—";
        setAlignment(sheet.getCell(currentRow, 8), { horizontal: "center", vertical: "middle" });

        currentRow++;
      });
    }

    applyBordersToRange(sheet, tableHeaderRow, 1, currentRow - 1, headers.length);
    sheet.getColumn(1).width = 7;
    sheet.getColumn(2).width = 12;
    sheet.getColumn(3).width = 24;
    sheet.getColumn(4).width = 14;
    sheet.getColumn(5).width = 48;
    sheet.getColumn(6).width = 18;
    sheet.getColumn(7).width = 12;
    sheet.getColumn(8).width = 15;

    sheet.views = [{ state: "frozen", ySplit: tableHeaderRow }];
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const fileName = `SO_THEO_DOI_TUAN_${classInfo.className || "Lop"}_${moment(dateRange[0]).format("YYYYMMDD")}_${moment(dateRange[1]).format("YYYYMMDD")}_${getExportTimestamp()}.xlsx`;
  saveAs(new Blob([buffer]), fileName);
}

/**
 * 2.3 Xuất sổ đánh giá Tháng Multi-Sheet (Mỗi HS là 1 Sheet)
 */
export async function exportMonthlyStudentSheetsExcel({ classInfo = {}, monthRange = [], studentsData = [] }) {
  const workbook = new ExcelJS.Workbook();
  const fromMonthDisplay = moment(monthRange[0], ["YYYY-MM", "YYYY-MM-DD"]).format("MM/YYYY");
  const toMonthDisplay = moment(monthRange[1], ["YYYY-MM", "YYYY-MM-DD"]).format("MM/YYYY");

  studentsData.forEach((student, index) => {
    const sheetName = sanitizeSheetName(student.studentName, index + 1);
    const sheet = workbook.addWorksheet(sheetName);

    const startRow = renderBannerHeader(sheet, {
      title: "SỔ ĐÁNH GIÁ NHẬN XÉT HỌC SINH THEO THÁNG",
      schoolName: classInfo.schoolName,
      className: classInfo.className,
      dateDisplay: `Từ tháng ${fromMonthDisplay} đến tháng ${toMonthDisplay}`,
      extraInfo: `Học sinh: ${student.studentName}    |    Mã HS: ${student.studentCode || `HS${student.studentId || index + 1}`}`,
    });

    const monthlyList = student.monthlyEvaluations || [];
    const headers = ["STT", "Tháng / Năm", "Tình trạng duyệt", "Nội dung nhận xét tháng", "Giáo viên nhận xét", "File đính kèm", "Phản hồi"];

    const tableHeaderRow = startRow + 1;
    sheet.getRow(tableHeaderRow).height = 28;

    headers.forEach((headerText, colIdx) => {
      const cell = sheet.getCell(tableHeaderRow, colIdx + 1);
      cell.value = headerText;
      setFontStyle(cell, { bold: true, size: 11, color: COLORS.WHITE });
      fillCell(cell, COLORS.BRAND_BLUE);
      setAlignment(cell, { horizontal: "center", vertical: "middle" });
    });

    let currentRow = tableHeaderRow + 1;
    if (monthlyList.length === 0) {
      sheet.mergeCells(currentRow, 1, currentRow, headers.length);
      const emptyCell = sheet.getCell(currentRow, 1);
      emptyCell.value = "Chưa có dữ liệu nhận xét tháng trong khoảng thời gian này.";
      setFontStyle(emptyCell, { italic: true, color: COLORS.MUTED_TEXT });
      setAlignment(emptyCell, { horizontal: "center", vertical: "middle" });
      currentRow++;
    } else {
      monthlyList.forEach((ev, idx) => {
        sheet.getRow(currentRow).height = 42;
        sheet.getCell(currentRow, 1).value = idx + 1;
        setAlignment(sheet.getCell(currentRow, 1), { horizontal: "center", vertical: "middle" });

        const mLabel = ev.month || ev.date ? moment(ev.month || ev.date).format("MM/YYYY") : `Tháng ${idx + 1}`;
        sheet.getCell(currentRow, 2).value = `Tháng ${mLabel}`;
        setFontStyle(sheet.getCell(currentRow, 2), { bold: true });
        setAlignment(sheet.getCell(currentRow, 2), { horizontal: "center", vertical: "middle" });

        const isApproved = ev.approved === true || ev.status === "APPROVED";
        const statusCell = sheet.getCell(currentRow, 3);
        statusCell.value = isApproved ? "Đã duyệt" : "Chờ duyệt";
        setFontStyle(statusCell, { bold: true, color: isApproved ? COLORS.GREEN_TEXT : COLORS.AMBER_TEXT, size: 10 });
        fillCell(statusCell, isApproved ? COLORS.GREEN_APPROVED : COLORS.AMBER_PENDING);
        setAlignment(statusCell, { horizontal: "center", vertical: "middle" });

        const contentCell = sheet.getCell(currentRow, 4);
        contentCell.value = ev.content || "—";
        setAlignment(contentCell, { horizontal: "left", vertical: "middle", wrapText: true, indent: 1 });

        sheet.getCell(currentRow, 5).value = ev.teacherName || ev.createdBy || "Giáo viên";
        setAlignment(sheet.getCell(currentRow, 5), { horizontal: "center", vertical: "middle" });

        const fileCount = getFileCount(ev);
        sheet.getCell(currentRow, 6).value = fileCount > 0 ? `${fileCount} file` : "—";
        setAlignment(sheet.getCell(currentRow, 6), { horizontal: "center", vertical: "middle" });

        const replyCount = getReplyCount(ev);
        sheet.getCell(currentRow, 7).value = replyCount > 0 ? `${replyCount} phản hồi` : "—";
        setAlignment(sheet.getCell(currentRow, 7), { horizontal: "center", vertical: "middle" });

        currentRow++;
      });
    }

    applyBordersToRange(sheet, tableHeaderRow, 1, currentRow - 1, headers.length);
    sheet.getColumn(1).width = 7;
    sheet.getColumn(2).width = 18;
    sheet.getColumn(3).width = 16;
    sheet.getColumn(4).width = 50;
    sheet.getColumn(5).width = 18;
    sheet.getColumn(6).width = 14;
    sheet.getColumn(7).width = 16;

    sheet.views = [{ state: "frozen", ySplit: tableHeaderRow }];
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const fileName = `SO_DANH_GIA_THANG_${classInfo.className || "Lop"}_${moment(monthRange[0], ["YYYY-MM", "YYYY-MM-DD"]).format("YYYYMM")}_${moment(monthRange[1], ["YYYY-MM", "YYYY-MM-DD"]).format("YYYYMM")}_${getExportTimestamp()}.xlsx`;
  saveAs(new Blob([buffer]), fileName);
}

/**
 * 2.4 Xuất sổ đánh giá Định kỳ Multi-Sheet (Mỗi HS là 1 Sheet)
 */
export async function exportPeriodicStudentSheetsExcel({ classInfo = {}, year, studentsData = [] }) {
  const workbook = new ExcelJS.Workbook();

  studentsData.forEach((student, index) => {
    const sheetName = sanitizeSheetName(student.studentName, index + 1);
    const sheet = workbook.addWorksheet(sheetName);

    const startRow = renderBannerHeader(sheet, {
      title: "SỔ ĐÁNH GIÁ NHẬN XÉT ĐỊNH KỲ HỌC SINH",
      schoolName: classInfo.schoolName,
      className: classInfo.className,
      dateDisplay: `Năm học / Năm ${year}`,
      extraInfo: `Học sinh: ${student.studentName}    |    Trạng thái: ${student.kidStatus === "STUDYING" ? "Đang học" : "Đã chuyển"}`,
    });

    const evals = student.evaluations || [];
    const headers = ["STT", "Lần nhận xét", "Thời gian đánh giá", "Trạng thái duyệt", "Nội dung nhận xét định kỳ", "Người đánh giá", "Phản hồi"];

    const tableHeaderRow = startRow + 1;
    sheet.getRow(tableHeaderRow).height = 28;

    headers.forEach((headerText, colIdx) => {
      const cell = sheet.getCell(tableHeaderRow, colIdx + 1);
      cell.value = headerText;
      setFontStyle(cell, { bold: true, size: 11, color: COLORS.WHITE });
      fillCell(cell, COLORS.BRAND_BLUE);
      setAlignment(cell, { horizontal: "center", vertical: "middle" });
    });

    let currentRow = tableHeaderRow + 1;
    if (evals.length === 0) {
      sheet.mergeCells(currentRow, 1, currentRow, headers.length);
      const emptyCell = sheet.getCell(currentRow, 1);
      emptyCell.value = "Chưa có nhận xét định kỳ nào trong năm học này.";
      setFontStyle(emptyCell, { italic: true, color: COLORS.MUTED_TEXT });
      setAlignment(emptyCell, { horizontal: "center", vertical: "middle" });
      currentRow++;
    } else {
      evals.forEach((ev, idx) => {
        sheet.getRow(currentRow).height = 46;
        sheet.getCell(currentRow, 1).value = idx + 1;
        setAlignment(sheet.getCell(currentRow, 1), { horizontal: "center", vertical: "middle" });

        sheet.getCell(currentRow, 2).value = `Lần ${ev.periodNumber || idx + 1}`;
        setFontStyle(sheet.getCell(currentRow, 2), { bold: true });
        setAlignment(sheet.getCell(currentRow, 2), { horizontal: "center", vertical: "middle" });

        sheet.getCell(currentRow, 3).value = ev.createdDate ? moment(ev.createdDate).format("DD/MM/YYYY HH:mm") : `Năm ${year}`;
        setAlignment(sheet.getCell(currentRow, 3), { horizontal: "center", vertical: "middle" });

        const isApproved = ev.approved === true;
        const statusCell = sheet.getCell(currentRow, 4);
        statusCell.value = isApproved ? "Đã duyệt" : "Chờ duyệt";
        setFontStyle(statusCell, { bold: true, color: isApproved ? COLORS.GREEN_TEXT : COLORS.AMBER_TEXT, size: 10 });
        fillCell(statusCell, isApproved ? COLORS.GREEN_APPROVED : COLORS.AMBER_PENDING);
        setAlignment(statusCell, { horizontal: "center", vertical: "middle" });

        const contentCell = sheet.getCell(currentRow, 5);
        contentCell.value = ev.content || "—";
        setAlignment(contentCell, { horizontal: "left", vertical: "middle", wrapText: true, indent: 1 });

        sheet.getCell(currentRow, 6).value = ev.teacherName || ev.createdBy || "Giáo viên";
        setAlignment(sheet.getCell(currentRow, 6), { horizontal: "center", vertical: "middle" });

        const replyCount = getReplyCount(ev);
        sheet.getCell(currentRow, 7).value = replyCount > 0 ? `${replyCount} phản hồi` : "—";
        setAlignment(sheet.getCell(currentRow, 7), { horizontal: "center", vertical: "middle" });

        currentRow++;
      });
    }

    applyBordersToRange(sheet, tableHeaderRow, 1, currentRow - 1, headers.length);
    sheet.getColumn(1).width = 7;
    sheet.getColumn(2).width = 16;
    sheet.getColumn(3).width = 20;
    sheet.getColumn(4).width = 16;
    sheet.getColumn(5).width = 52;
    sheet.getColumn(6).width = 18;
    sheet.getColumn(7).width = 16;

    sheet.views = [{ state: "frozen", ySplit: tableHeaderRow }];
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const fileName = `SO_DANH_GIA_DINH_KY_${classInfo.className || "Lop"}_Nam_${year}_${getExportTimestamp()}.xlsx`;
  saveAs(new Blob([buffer]), fileName);
}

/* ==========================================================================
   MOCK DEMO DATA GENERATOR (PHỤC VỤ KIỂM THỬ XUẤT EXCEL NGAY TẠI FE)
   ========================================================================== */

/**
 * Sinh dữ liệu mẫu cho danh sách học sinh theo khoảng ngày/tuần/tháng/định kỳ
 */
export function generateMockStudentRangeData({ studentList = [], type = "DAILY", dateRange = [], year = "2026" }) {
  const sampleCategories = [
    { categoryId: 1, categoryName: "Học tập", contents: ["Con chú ý nghe giảng, hăng hái phát biểu.", "Con thuộc bài hát mới và tham gia vẽ tranh hào hứng.", "Tiếp thu tốt, trả lời đúng câu hỏi của cô."] },
    { categoryId: 2, categoryName: "Ăn uống", contents: ["Ăn hết suất, tự xúc ăn gọn gàng.", "Ăn ngon miệng, uống đủ nước.", "Ăn tốt, không làm rơi vãi cơm."] },
    { categoryId: 3, categoryName: "Ngủ nghỉ", contents: ["Ngủ đúng giờ, ngủ say và đủ giấc.", "Ngủ ngoan, dậy đúng giờ không quấy khóc.", "Ngủ tốt."] },
    { categoryId: 4, categoryName: "Vệ sinh", contents: ["Biết tự đi vệ sinh đúng nơi quy định.", "Tự giác rửa tay sạch trước khi ăn.", "Rất gọn gàng và ngăn nắp."] },
    { categoryId: 5, categoryName: "Sức khỏe", contents: ["Sức khỏe bình thường, nhiệt độ 36.5°C.", "Nhanh nhẹn, thể lực tốt.", "Khỏe mạnh, vui vẻ tham gia hoạt động."] },
    { categoryId: 6, categoryName: "Nhận xét chung", contents: ["Bé rất ngoan, đoàn kết và giúp đỡ bạn bè.", "Hôm nay bé có nhiều tiến bộ đáng khen.", "Bé tự tin hòa đồng với cả lớp."] },
  ];

  const sampleWeeklyContents = [
    "Tuần qua con tham gia tích cực các hoạt động nhóm, biết lắng nghe cô và hòa đồng với các bạn. Kỹ năng vận động tinh có nhiều tiến bộ.",
    "Con nắm vững các bài học trong tuần, hát hay múa đẹp, tự tin biểu diễn trước lớp. Ăn ngủ đúng nề nếp.",
    "Tuần này con rất ngoan, tự giác cất đồ chơi sau khi dùng xong, biết chia sẻ đồ chơi với bạn.",
    "Con chăm chỉ học tập, tiếp thu nhanh các bài học nhận biết mặt chữ và số. Thể lực tốt.",
  ];

  const sampleMonthlyContents = [
    "Trong tháng bé đạt 100% các mục tiêu phát triển thể chất và thẩm mỹ. Kỹ năng giao tiếp tự tin, biết diễn đạt rõ ràng cảm xúc và suy nghĩ.",
    "Bé hoàn thành xuất sắc các chuyên đề học tập trong tháng. Biết tự lập trong sinh hoạt cá nhân, ăn uống ngon miệng.",
    "Tháng này con tiến bộ vượt bậc về nhận thức và tư duy logic. Rất lễ phép với thầy cô và thân thiện với bạn bè.",
  ];

  const samplePeriodicContents = [
    "Lần 1: Bé phát triển toàn diện theo chuẩn độ tuổi mầm non. Đạt xuất sắc các chỉ số vận động thô và ngôn ngữ. Thái độ vui vẻ, yêu thích đến trường.",
    "Lần 2: Bé hình thành tốt các kỹ năng tiền tiểu học, tự giác kỷ luật cao. Kỹ năng thẩm mỹ và tạo hình nổi trội.",
    "Lần 3: Đánh giá cuối kỳ bé hoàn thành tốt toàn bộ các tiêu chí giáo dục mầm non, sẵn sàng tâm thế bước vào cấp học mới.",
  ];

  return studentList.map((student, sIdx) => {
    const sId = student.studentId || sIdx + 1;
    const sName = student.studentName || `Học sinh ${sId}`;

    if (type === "DAILY") {
      const startDate = moment(dateRange[0] || moment().startOf("month"));
      const endDate = moment(dateRange[1] || moment());
      const dailyEvaluations = [];

      let cur = startDate.clone();
      while (cur.isSameOrBefore(endDate)) {
        if (cur.isoWeekday() <= 5) { // Chỉ lấy từ T2 đến T6
          const dayItems = sampleCategories.map((cat) => ({
            categoryId: cat.categoryId,
            categoryName: cat.categoryName,
            content: cat.contents[(sIdx + cur.date()) % cat.contents.length],
          }));

          dailyEvaluations.push({
            date: cur.format("YYYY-MM-DD"),
            approved: (cur.date() + sIdx) % 3 !== 0,
            teacherName: sIdx % 2 === 0 ? "Cô Lan Hương" : "Cô Mai Anh",
            items: dayItems,
            files: (cur.date() % 2 === 0) ? [{ name: "anh_hoat_dong.jpg" }] : [],
            replies: (cur.date() % 3 === 0) ? [{ content: "Cảm ơn cô giáo đã quan tâm đến con!", replierName: "Phụ huynh" }] : [],
          });
        }
        cur.add(1, "day");
      }
      return { studentId: sId, studentName: sName, studentCode: `HS${1000 + sId}`, dailyEvaluations };
    }

    if (type === "WEEKLY") {
      const startDate = moment(dateRange[0] || moment().startOf("month"));
      const endDate = moment(dateRange[1] || moment());
      const weeklyEvaluations = [];

      let cur = startDate.clone().startOf("isoWeek");
      let wCount = 1;
      while (cur.isSameOrBefore(endDate)) {
        weeklyEvaluations.push({
          weekNumber: cur.isoWeek(),
          fromDate: cur.format("YYYY-MM-DD"),
          toDate: cur.clone().endOf("isoWeek").format("YYYY-MM-DD"),
          approved: true,
          content: sampleWeeklyContents[(sIdx + wCount) % sampleWeeklyContents.length],
          teacherName: "Cô Thu Hà",
          files: wCount % 2 === 0 ? [{ name: "san_pham_be.png" }] : [],
          replies: wCount % 2 === 1 ? [{ content: "Bé về nhà rất hào hứng kể về bài học!", replierName: "Mẹ bé" }] : [],
        });
        wCount++;
        cur.add(1, "week");
      }
      return { studentId: sId, studentName: sName, studentCode: `HS${1000 + sId}`, weeklyEvaluations };
    }

    if (type === "MONTHLY") {
      const startMonth = moment(dateRange[0] || moment().startOf("year"), ["YYYY-MM", "YYYY-MM-DD"]);
      const endMonth = moment(dateRange[1] || moment(), ["YYYY-MM", "YYYY-MM-DD"]);
      const monthlyEvaluations = [];

      let cur = startMonth.clone().startOf("month");
      let mCount = 1;
      while (cur.isSameOrBefore(endMonth)) {
        monthlyEvaluations.push({
          month: cur.format("YYYY-MM"),
          approved: true,
          content: sampleMonthlyContents[(sIdx + mCount) % sampleMonthlyContents.length],
          teacherName: "Cô Mai Anh",
          files: [{ name: "bang_danh_gia_thang.pdf" }],
          replies: mCount % 2 === 0 ? [{ content: "Gia đình cảm ơn các cô rất nhiều!", replierName: "Bố bé" }] : [],
        });
        mCount++;
        cur.add(1, "month");
      }
      return { studentId: sId, studentName: sName, studentCode: `HS${1000 + sId}`, monthlyEvaluations };
    }

    if (type === "PERIODIC") {
      const evaluations = [
        { periodNumber: 1, approved: true, content: samplePeriodicContents[0], createdDate: `${year}-03-15 10:00:00`, teacherName: "Cô Lan Hương", replies: [] },
        { periodNumber: 2, approved: true, content: samplePeriodicContents[1], createdDate: `${year}-05-20 14:30:00`, teacherName: "Cô Lan Hương", replies: [{ content: "Bé rất tiến bộ!", replierName: "Phụ huynh" }] },
        { periodNumber: 3, approved: sIdx % 2 === 0, content: samplePeriodicContents[2], createdDate: `${year}-11-10 09:15:00`, teacherName: "Cô Lan Hương", replies: [] },
      ];
      return { studentId: sId, studentName: sName, studentCode: `HS${1000 + sId}`, kidStatus: "STUDYING", evaluations };
    }

    return { studentId: sId, studentName: sName };
  });
}

export default {
  exportDailyClassExcel,
  exportWeeklyClassExcel,
  exportMonthlyClassExcel,
  exportPeriodicClassExcel,
  exportDailyStudentSheetsExcel,
  exportWeeklyStudentSheetsExcel,
  exportMonthlyStudentSheetsExcel,
  exportPeriodicStudentSheetsExcel,
  generateMockStudentRangeData,
};
